/** Shared graph-first guide flow utilities (report, case, future modules). */

export function normalizeGuideTemplateWithFlow(template) {
  if (!template) return null;

  const questions = (template.questions || []).map((question) => ({
    ...question,
    options: normalizeOptions(question),
  }));
  const settings = template.settings || {};
  const flow = normalizeFlow(settings.flow || buildFlowFromQuestions(questions), questions);

  return {
    ...template,
    settings: {
      ...settings,
      flow,
    },
    questions: questionsFromFlow(flow, questions),
  };
}

export function buildFlowFromQuestions(questions = []) {
  const activeQuestions = questions.filter((question) => question.active !== false);
  const nodes = activeQuestions.map((question, index) => ({
    id: question.key || `question_${index + 1}`,
    type: "question",
    title: question.label || question.key || `Question ${index + 1}`,
    x: 100 + index * 280,
    y: 120,
    data: {
      record_id: question.id,
      key: question.key || `question_${index + 1}`,
      label: question.label || "",
      help_text: question.help_text || "",
      answer_type: question.answer_type || "single_select",
      required: Boolean(question.required),
      include_in_report: question.conditions?.include_in_report === true,
      options: normalizeOptions(question),
      effects: asArray(question.effects).filter((effect) => !effect.when_value),
    },
  }));

  const edges = [];
  nodes.forEach((node, index) => {
    const options = asArray(node.data.options);
    options.forEach((option) => {
      if (!option.next_node_id) return;
      edges.push({
        id: `${node.id}-${option.value}-${option.next_node_id}`,
        source: node.id,
        target: option.next_node_id,
        when: { value: option.value },
      });
    });

    if (options.length === 0 && nodes[index + 1]) {
      edges.push({
        id: `${node.id}-next-${nodes[index + 1].id}`,
        source: node.id,
        target: nodes[index + 1].id,
        when: { always: true },
      });
    }
  });

  return {
    version: 1,
    start_node_id: nodes[0]?.id || null,
    nodes,
    edges,
  };
}

export function questionsFromFlow(flow, fallbackQuestions = []) {
  const normalizedFlow = normalizeFlow(flow, fallbackQuestions);
  return normalizedFlow.nodes
    .filter((node) => node.type === "question")
    .map((node, index) => ({
      id: node.data?.record_id,
      key: node.data?.key || node.id,
      label: node.data?.label || node.title || "",
      help_text: node.data?.help_text || "",
      answer_type: node.data?.answer_type || "single_select",
      options: asArray(node.data?.options),
      required: Boolean(node.data?.required),
      sort_order: index,
      conditions: { include_in_report: node.data?.include_in_report === true },
      effects: asArray(node.data?.effects),
      active: true,
    }));
}

/**
 * Map JSON coordinates to numbers; treat null / undefined / "" as missing so we don't cluster at 0
 * (`Number(null) === 0` would otherwise collapse imported AI flows).
 */
export function normalizeGuideFlowAxis(value, fallback) {
  if (value === null || value === undefined || value === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function normalizeFlow(flow, fallbackQuestions = []) {
  const sourceFlow =
    flow && Array.isArray(flow.nodes)
      ? flow
      : buildFlowFromQuestions(fallbackQuestions);
  const nodes = asArray(sourceFlow.nodes).map((node, index) => {
    const baseData = node.data || {};
    const rawEffects = baseData.effects;
    /** Effect nodes store `data.effects` as a plain object; questions keep an array of legacy rules. */
    let normalizedEffects;
    if ((node.type || "question") === "effect") {
      normalizedEffects =
        rawEffects && typeof rawEffects === "object" && !Array.isArray(rawEffects) ? { ...rawEffects } : {};
    } else {
      normalizedEffects = Array.isArray(rawEffects) ? [...rawEffects] : [];
    }

    return {
      id: node.id || `node_${index + 1}`,
      type: node.type || "question",
      title: node.title || node.data?.label || `Question ${index + 1}`,
      x: normalizeGuideFlowAxis(node.x, 100 + index * 280),
      y: normalizeGuideFlowAxis(node.y, 120),
      data: {
        ...baseData,
        options: asArray(baseData.options),
        effects: normalizedEffects,
      },
    };
  });

  return {
    version: sourceFlow.version || 1,
    viewport: normalizeViewport(sourceFlow.viewport),
    start_node_id: sourceFlow.start_node_id || nodes[0]?.id || null,
    nodes,
    edges: asArray(sourceFlow.edges),
  };
}

export function getQuestionNodes(flow) {
  return asArray(flow?.nodes).filter((node) => node.type === "question");
}

export function getFlowNode(flow, nodeId) {
  if (nodeId === undefined || nodeId === null || nodeId === "") return null;
  return asArray(flow?.nodes).find((node) => String(node.id) === String(nodeId)) || null;
}

export function getCurrentQuestion(flow, currentNodeId) {
  if (currentNodeId === undefined || currentNodeId === null || currentNodeId === "") return null;
  return getQuestionNodes(flow).find((node) => String(node.id) === String(currentNodeId)) || null;
}

/** Match answers to option values after JSON/API coercion (number vs string, etc.). */
export function guideFlowValuesLooselyEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

export function guideFlowAnswerMatchesOption(answer, optionValue) {
  if (Array.isArray(answer)) {
    return answer.some((item) => guideFlowValuesLooselyEqual(item, optionValue));
  }
  return guideFlowValuesLooselyEqual(answer, optionValue);
}

/**
 * True when the current answer maps to an option that sets `next_node_id` (including multi-select: any chosen value).
 * Used to decide whether linear fallback may run: we must not jump to the next question in list when this answer
 * explicitly targets another node (even if that target is broken — fix the template instead).
 */
export function selectedAnswerDeclaresNextNodeId(questionNode, answerValue) {
  const opts = asArray(questionNode?.data?.options);
  if (opts.length === 0) return false;

  const values = Array.isArray(answerValue)
    ? answerValue
    : answerValue === undefined || answerValue === null || answerValue === ""
      ? []
      : [answerValue];

  if (values.length === 0) return false;

  return values.some((val) => {
    const matching = opts.find((o) => guideFlowValuesLooselyEqual(val, o.value));
    return Boolean(matching?.next_node_id);
  });
}

/**
 * Resolve next question after answering the current one (graph traversal + optional linear fallback).
 * Fallback runs when the graph gives no next question **for this answer** and the chosen option does not declare
 * `next_node_id`. If another option on the same question has a branch but this one does not, we still fall back
 * to linear order so the guide does not end prematurely.
 */
export function resolveGuideQuestionNavigation(flow, currentQuestionNode, answerValue, answers) {
  if (!currentQuestionNode || (currentQuestionNode.type && currentQuestionNode.type !== "question")) {
    return { graphNextNodeId: null, nextQuestionNodeId: null };
  }

  const graphNextNodeId = getNextNodeId(flow, currentQuestionNode, answerValue);
  let nextQuestionNodeId =
    graphNextNodeId != null ? getNextQuestionNodeId(flow, graphNextNodeId, answers) : null;

  const selectedDeclaresGraphEdge = selectedAnswerDeclaresNextNodeId(currentQuestionNode, answerValue);
  if (graphNextNodeId === null && nextQuestionNodeId === null && !selectedDeclaresGraphEdge) {
    const ordered = getQuestionNodes(flow);
    const idx = ordered.findIndex((n) => String(n.id) === String(currentQuestionNode.id));
    if (idx >= 0 && idx < ordered.length - 1) {
      nextQuestionNodeId = ordered[idx + 1].id;
    }
  }

  return { graphNextNodeId, nextQuestionNodeId };
}

export function getStartNodeId(flow) {
  return flow?.start_node_id || asArray(flow?.nodes)[0]?.id || null;
}

export function getNextNodeId(flow, node, answer) {
  if (!node) return null;
  if (node.type === "effect") {
    if (node.data?.next_node_id) return node.data.next_node_id;
    return (
      asArray(flow?.edges).find((edge) => String(edge.source) === String(node.id) && edge.when?.always)?.target ||
      null
    );
  }

  const matchingOption = asArray(node.data?.options).find((option) =>
    guideFlowAnswerMatchesOption(answer, option.value)
  );
  if (matchingOption?.next_node_id) return matchingOption.next_node_id;

  const matchingEdge = asArray(flow?.edges).find((edge) => {
    if (String(edge.source) !== String(node.id)) return false;
    if (edge.when?.always) return true;
    if (edge.when?.value === undefined || edge.when?.value === null) return false;
    return guideFlowAnswerMatchesOption(answer, edge.when.value);
  });

  return matchingEdge?.target || null;
}

export function getNextQuestionNodeId(flow, fromNodeId, answers) {
  let currentNodeId = fromNodeId;
  const seen = new Set();

  while (currentNodeId && !seen.has(String(currentNodeId))) {
    seen.add(String(currentNodeId));
    const node = getFlowNode(flow, currentNodeId);
    if (!node) return null;
    if (node.type === "question") return node.id;

    currentNodeId = getNextNodeId(flow, node, answers?.[node.data?.key || node.id]);
  }

  return null;
}

export function getVisitedFlowNodes(flow, answers) {
  const result = [];
  const seen = new Set();
  let currentNodeId = getStartNodeId(flow);

  while (currentNodeId && !seen.has(String(currentNodeId))) {
    seen.add(String(currentNodeId));
    const node = getFlowNode(flow, currentNodeId);
    if (!node) break;
    result.push(node);

    if (node.type === "question") {
      const answer = answers?.[node.data?.key || node.id];
      if (answer === undefined || answer === null || answer === "") break;
      let nextId = getNextNodeId(flow, node, answer);
      if (nextId == null && !selectedAnswerDeclaresNextNodeId(node, answer)) {
        const ordered = getQuestionNodes(flow);
        const idx = ordered.findIndex((n) => String(n.id) === String(node.id));
        if (idx >= 0 && idx < ordered.length - 1) {
          nextId = ordered[idx + 1].id;
        }
      }
      currentNodeId = nextId;
    } else {
      currentNodeId = getNextNodeId(flow, node, null);
    }
  }

  return result;
}

export function getVisitedQuestionNodes(flow, answers) {
  return getVisitedFlowNodes(flow, answers).filter((node) => node.type === "question");
}

/**
 * @param {Function} mergeEffect — (effectsAcc, effectPayload) => void; mutates effectsAcc.
 */
export function collectFlowEffectsFromTemplate(template, answers, mergeEffect) {
  const flow = template?.settings?.flow;
  const effects = { tag_ids: [] };

  getVisitedFlowNodes(flow, answers).forEach((node) => {
    if (node.type === "effect") {
      if (node.data?.effects && !Array.isArray(node.data.effects)) mergeEffect(effects, node.data.effects);
      asArray(node.data?.effects).forEach((effect) => mergeEffect(effects, effect));
      return;
    }

    const key = node.data?.key || node.id;
    const answer = answers?.[key];
    if (!answered(answer)) return;

    asArray(node.data?.effects).forEach((effect) => mergeEffect(effects, effect));
    asArray(node.data?.options).forEach((option) => {
      if (!optionApplies(option, answer)) return;
      asArray(option.effects).forEach((effect) => mergeEffect(effects, effect));
      if (option.effects && !Array.isArray(option.effects)) mergeEffect(effects, option.effects);
    });
  });

  effects.tag_ids = [...new Set((effects.tag_ids || []).filter(Boolean))];
  return effects;
}

export function normalizeOptions(question) {
  const effectByValue = {};
  asArray(question.effects).forEach((effect) => {
    const values = Array.isArray(effect.when_value) ? effect.when_value : [effect.when_value];
    values.filter((value) => value !== undefined && value !== null).forEach((value) => {
      effectByValue[String(value)] = stripWhenValue(effect);
    });
  });

  return asArray(question.options).map((option) => {
    const value = option.value ?? option.id ?? option.label;
    return {
      label: option.label ?? String(value ?? ""),
      value,
      next_node_id: option.next_node_id || option.nextNodeId || option.next_question_key || "",
      effects: option.effects || effectByValue[String(value)] || {},
    };
  });
}

function stripWhenValue(effect) {
  const { when_value: _whenValue, ...rest } = effect || {};
  return rest;
}

function optionApplies(option, answer) {
  const values = Array.isArray(answer) ? answer.map(String) : [answer].filter((value) => value !== undefined && value !== null).map(String);
  return values.includes(String(option.value));
}

function answered(value) {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

export function normalizeViewport(viewport) {
  const x = Number(viewport?.x);
  const y = Number(viewport?.y);
  const zoom = Number(viewport?.zoom);

  return {
    x: Number.isFinite(x) ? x : 80,
    y: Number.isFinite(y) ? y : 40,
    zoom: Number.isFinite(zoom) ? zoom : 1,
  };
}
